import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, Row, Col } from 'antd';
import { Eye, EyeOff, X, ChevronDown } from 'lucide-react';
import type { Department } from '../../types';
import type { Personnel } from '../../types/personnel';
import { ManagerOption, Role } from './AddPersonnelModal';
import styles from './PersonnelModal.module.css';

const { Option } = Select;

export interface EditPersonnelFormData {
  fullName: string;
  email: string;
  department: string;
  position: string;
  workplace?: string;
  password?: string;
  confirmPassword?: string;
  role: string;
  managerId?: number | null;
}

export interface EditPersonnelModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EditPersonnelFormData) => void;
  departments: Department[];
  roles?: Role[];
  managers?: ManagerOption[];
  canAssignRole?: boolean;
  editableFields?: string[];
  initialData?: Personnel;
  initialEmail?: string;
}

const DEFAULT_ROLES: Role[] = [
  { id: 'admin', name: 'Quản trị viên', permissions: ['all'] },
  { id: 'ceo', name: 'Giám đốc', permissions: ['view', 'edit', 'approve'] },
  { id: 'hr', name: 'Nhân sự', permissions: ['view', 'edit'] },
  { id: 'staff', name: 'Nhân viên', permissions: ['view'] },
];

const passwordIconRender = (visible: boolean) =>
  visible ? (
    <EyeOff size={16} className={styles.eyeIcon} />
  ) : (
    <Eye size={16} className={styles.eyeIcon} />
  );

const passwordValidator = (_: unknown, value: string) => {
  if (!value || value.length >= 6) {
    return Promise.resolve();
  }
  return Promise.reject(new Error('Mật khẩu phải có ít nhất 6 ký tự'));
};

export const EditPersonnelModal: React.FC<EditPersonnelModalProps> = ({
  open,
  onClose,
  onSubmit,
  departments,
  roles = DEFAULT_ROLES,
  managers = [],
  canAssignRole = false,
  editableFields,
  initialData,
  initialEmail,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const canEditField = (field: string) =>
    !editableFields || editableFields.includes(field);

  useEffect(() => {
    if (open && initialData) {
      // Map role từ backend format (admin/ceo/hr/staff) sang UI
      // initialData.role chứa role từ backend
      const userRole = initialData.role || 'staff'; // Default là staff nếu không có
      const role = roles.find(r => r.id === String(initialData.roleId))
        || roles.find(r => r.code === userRole || r.id === userRole)
        || roles[roles.length - 1]; // Tìm role theo DB ID, fallback role name cũ
      
      form.setFieldsValue({
        fullName: initialData.fullName,
        email: initialEmail || initialData.email || '',
        department: initialData.company,
        position: initialData.jobTitle,
        workplace: initialData.workplace,
        role: role?.id, // Map từ user.role thực tế
        managerId: initialData.managerId ?? 0,
        password: undefined,
        confirmPassword: undefined,
      });
    }
  }, [open, initialData, initialEmail, roles, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(values);
      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const confirmPasswordValidator = (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    const password = form.getFieldValue('password');
    if (value && password && value !== password) {
      return Promise.reject(new Error('Mật khẩu xác nhận không khớp'));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      closeIcon={<X size={20} />}
      className={styles.modal}
      width={800}
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Chỉnh sửa thông tin nhân sự</h2>

        <Form form={form} layout="vertical" onFinish={handleSubmit} className={styles.form}>
          <Row gutter={24}>
            {canEditField('fullName') && <Col span={12}>
              <Form.Item
                name="fullName"
                label="Họ và tên *"
                rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
              >
                <Input placeholder="Nhập họ và tên" size="large" className={styles.input} />
              </Form.Item>
            </Col>}

            {canEditField('email') && <Col span={12}>
              <Form.Item
                name="email"
                label="Email *"
                rules={[
                  { required: true, message: 'Vui lòng nhập email' },
                  { type: 'email', message: 'Email không hợp lệ' },
                ]}
              >
                <Input placeholder="Email" size="large" className={styles.input} />
              </Form.Item>
            </Col>}
          </Row>

          <Row gutter={24}>
            {canEditField('departmentId') && <Col span={8}>
              <Form.Item
                name="department"
                label="Phòng ban *"
                rules={[{ required: true, message: 'Vui lòng chọn phòng ban' }]}
              >
                <Select
                  placeholder="Chọn phòng ban"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.name}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>}

            {canEditField('position') && <Col span={8}>
              <Form.Item
                name="position"
                label="Vị trí *"
                rules={[{ required: true, message: 'Vui lòng nhập vị trí' }]}
              >
                <Input placeholder="Nhập vị trí" size="large" className={styles.input} />
              </Form.Item>
            </Col>}

            {canEditField('workplace') && <Col span={8}>
              <Form.Item name="workplace" label="Nơi làm việc">
                <Input placeholder="Nhập nơi làm việc" size="large" className={styles.input} />
              </Form.Item>
            </Col>}

            {canAssignRole && <Col span={8}>
              <Form.Item
                name="role"
                label="Chức vụ và phân quyền *"
                rules={[{ required: true, message: 'Vui lòng chọn chức vụ' }]}
              >
                <Select
                  placeholder="Chọn chức vụ"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                >
                  {roles.map((role) => (
                    <Option key={role.id} value={role.id}>
                      {role.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>}

            {canEditField('managerId') && <Col span={8}>
              <Form.Item name="managerId" label="Quản lý trực tiếp">
                <Select
                  placeholder="Chọn quản lý trực tiếp"
                  size="large"
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                  showSearch
                  optionFilterProp="label"
                  options={[
                    { value: 0, label: 'Chưa gán quản lý' },
                    ...managers.map((manager) => ({
                      value: manager.id,
                      label: `${manager.fullName}${manager.employeeCode ? ` (${manager.employeeCode})` : ''}`,
                      disabled: manager.id === Number(initialData?.id),
                    })),
                  ]}
                />
              </Form.Item>
            </Col>}
          </Row>

          {canAssignRole && <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Mật khẩu"
                rules={[{ validator: passwordValidator }]}
              >
                <Input.Password
                  placeholder="Nhập mật khẩu"
                  size="large"
                  className={styles.input}
                  iconRender={passwordIconRender}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Xác nhận mật khẩu"
                dependencies={['password']}
                rules={[{ validator: confirmPasswordValidator }]}
              >
                <Input.Password
                  placeholder="Nhập mật khẩu"
                  size="large"
                  className={styles.input}
                  iconRender={passwordIconRender}
                />
              </Form.Item>
            </Col>
          </Row>}

          <div className={styles.modalFooter}>
            <Button size="large" onClick={handleCancel} className={styles.cancelButton}>
              Huỷ
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={loading}
              className={styles.submitButton}
            >
              Cập nhật
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};
